var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentConference } from '../base/conference/functions';
import { hideNotification, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE, SALESFORCE_LINK_NOTIFICATION_ID } from '../notifications/constants';
import { executeLinkMeetingRequest, getRecentSessionRecords, getSessionRecordDetails, searchSessionRecords } from './functions';
export const useSalesforceLinkDialog = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedRecordOwner, setSelectedRecordOwner] = useState(null);
    const [records, setRecords] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(null);
    const [notes, setNotes] = useState('');
    const [hasRecordsErrors, setRecordsErrors] = useState(false);
    const [hasDetailsErrors, setDetailsErrors] = useState(false);
    const conference = useSelector(getCurrentConference);
    const sessionId = conference.getMeetingUniqueId();
    const { salesforceUrl = '' } = useSelector((state) => state['features/base/config']);
    const { jwt = '' } = useSelector((state) => state['features/base/jwt']);
    const showSearchResults = searchTerm && searchTerm.length > 1;
    const showNoResults = showSearchResults && records.length === 0;
    useEffect(() => {
        const fetchRecords = () => __awaiter(void 0, void 0, void 0, function* () {
            setRecordsErrors(false);
            setLoading(true);
            try {
                const text = showSearchResults ? searchTerm : null;
                const result = text
                    ? yield searchSessionRecords(salesforceUrl, jwt, text)
                    : yield getRecentSessionRecords(salesforceUrl, jwt);
                setRecords(result);
            }
            catch (error) {
                setRecordsErrors(true);
            }
            setLoading(false);
        });
        fetchRecords();
    }, [
        getRecentSessionRecords,
        jwt,
        salesforceUrl,
        searchSessionRecords,
        searchTerm
    ]);
    useEffect(() => {
        const fetchRecordDetails = () => __awaiter(void 0, void 0, void 0, function* () {
            setDetailsErrors(false);
            setSelectedRecordOwner(null);
            try {
                const result = yield getSessionRecordDetails(salesforceUrl, jwt, selectedRecord);
                setSelectedRecordOwner({
                    id: result.id,
                    name: result.ownerName,
                    type: 'OWNER'
                });
            }
            catch (error) {
                setDetailsErrors(true);
            }
        });
        selectedRecord && fetchRecordDetails();
    }, [
        jwt,
        getSessionRecordDetails,
        salesforceUrl,
        selectedRecord
    ]);
    const linkMeeting = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        dispatch(showNotification({
            titleKey: 'notify.linkToSalesforceProgress',
            uid: SALESFORCE_LINK_NOTIFICATION_ID,
            appearance: NOTIFICATION_TYPE.NORMAL
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
        try {
            yield executeLinkMeetingRequest(salesforceUrl, jwt, sessionId, {
                id: selectedRecord === null || selectedRecord === void 0 ? void 0 : selectedRecord.id,
                type: selectedRecord === null || selectedRecord === void 0 ? void 0 : selectedRecord.type,
                notes
            });
            dispatch(hideNotification(SALESFORCE_LINK_NOTIFICATION_ID));
            dispatch(showNotification({
                titleKey: 'notify.linkToSalesforceSuccess',
                uid: SALESFORCE_LINK_NOTIFICATION_ID,
                appearance: NOTIFICATION_TYPE.SUCCESS
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
        catch (error) {
            dispatch(hideNotification(SALESFORCE_LINK_NOTIFICATION_ID));
            dispatch(showNotification({
                titleKey: 'notify.linkToSalesforceError',
                descriptionKey: (error === null || error === void 0 ? void 0 : error.messageKey) && t(error.messageKey),
                uid: SALESFORCE_LINK_NOTIFICATION_ID,
                appearance: NOTIFICATION_TYPE.ERROR
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    }), [
        executeLinkMeetingRequest,
        hideNotification,
        jwt,
        notes,
        salesforceUrl,
        selectedRecord,
        showNotification
    ]);
    return {
        hasDetailsErrors,
        hasRecordsErrors,
        isLoading,
        linkMeeting,
        notes,
        records,
        searchTerm,
        selectedRecord,
        selectedRecordOwner,
        setNotes,
        setSearchTerm,
        setSelectedRecord,
        showNoResults,
        showSearchResults
    };
};
